import React, { Fragment, useContext, useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// Components

import EmailShow from './EmailShow';
import Paginator from '../../../miscellaneous/SecureInboxPaginator';
import SecureInboxTable from './SecureInboxTable';
import { FormattedMessage } from 'react-intl';

// ActionCreators

import { notificationsActionCreators } from '@actions/individualPortal/secureInbox/secureInboxActionCreators';
import { fetchEnlargedFontEnabled } from '@actions/accessibility/accessibilityActionCreators';

// Hooks

import { useInboxId } from '@hooks/useInboxId';
import { useQueryParams } from '@hooks/index';

import { InboxContext } from '@contexts/InboxContext';

const SecureInboxSummary = props => {
    const queryParams = useQueryParams();
    const {
        actions: {
            fetchNotifications,
            clearNotifications,
            fetchUnreadNotificationCount,
            updateNotificationsPagination,
            fetchEnlargedFontEnabled,
        },
        notifications: {
            content: notifications,
            pageable,
            pageable: { pageNumber },
            empty: isEmpty,
            first,
            last,
            totalPages,
        },
        environment: {
            impersonation = false,
            impersonationDetails,
            telexEnabled,
            user,
        },
        emailShown,
        setEmailShown,
        query = '',
        isLiteFormat = false,
        className = 'secure-inbox-wrapper',
    } = props;

    const paginatorData = {
        first,
        isEmpty,
        last,
        pageNumber,
        totalPages,
    };

    const changePage = newPage => {
        updateNotificationsPagination({
            pageable: {
                ...pageable,
                pageNumber: newPage,
            },
            first: newPage === 0,
            last: newPage === totalPages - 1,
        });
    };

    const { activeModuleId: householdId = 0, userId, role } = user || {};

    const hhIdFromQueryParams = queryParams.get('hhid');
    const inboxIdData = {
        householdId: hhIdFromQueryParams || householdId,
        impersonation,
        impersonationDetails,
        isInImpersonation: !!hhIdFromQueryParams,
        pageNumber,
        role,
        userId,
    };

    const { setLiteFormat, titleId } = useContext(InboxContext);

    const { part, inboxId } = useInboxId(inboxIdData);

    type CleanupSnapshot = {
        part: typeof part;
        inboxId: typeof inboxId;
        telexEnabled: typeof telexEnabled;
        user: typeof user;
    };
    const cleanupDataRef = useRef<CleanupSnapshot | null>(null);

    useEffect(() => {
        cleanupDataRef.current = { part, inboxId, telexEnabled, user };
    });

    useEffect(() => {
        return () => {
            clearNotifications();
            const latest = cleanupDataRef.current;
            if (
                latest?.telexEnabled &&
                latest?.user &&
                latest?.part != null &&
                latest?.inboxId != null
            ) {
                fetchUnreadNotificationCount(latest.part, latest.inboxId);
            }
        };
    }, []);

    useEffect(() => {
        if (!emailShown) {
            fetchNotifications(part, inboxId, pageNumber, query).catch(() => {});
            fetchUnreadNotificationCount(part, inboxId);
        }
        // `query` deliberately omitted: search submissions dispatch
        // fetchNotifications directly from NonIndSideNavigation; including it
        // here would double-fetch on every keystroke.
    }, [emailShown, pageNumber, part, inboxId]);

    const handleReadCompleted = () => {
        fetchNotifications(part, inboxId, pageNumber, query).catch(() => {});
        fetchUnreadNotificationCount(part, inboxId);
    };

    useEffect(() => {
        setLiteFormat(isLiteFormat);
        fetchEnlargedFontEnabled();
    }, []);

    const ShowTitle = () => {
        if (isLiteFormat) {
            return (
                <h2 className="inbox__title">
                    <FormattedMessage id={titleId ? titleId : 'common.notifications'} />
                </h2>
            );
        }

        return (
            <h1 className="inbox__title">
                <FormattedMessage id={titleId ? titleId : 'common.notifications'} />
            </h1>
        );
    };

    return (
        <div className={className}>
            <div className="grid-col-12">
                <ShowTitle />
            </div>

            {emailShown ? (
                <EmailShow {...{ emailShown, setEmailShown, onReadCompleted: handleReadCompleted }} />
            ) : (
                <Fragment>
                    <SecureInboxTable
                        {...{
                            notifications,
                            setEmailShown,
                        }}
                    />
                    <div className="pagination-container__secure-inbox">
                        <Paginator
                            {...{
                                changePage,
                                ...paginatorData,
                            }}
                        />
                    </div>
                </Fragment>
            )}
        </div>
    );
};

const mapStateToProps = state => ({
    notifications: state.individualPortal.secureInbox.notifications,
    environment: state.common.environment,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({ ...notificationsActionCreators, fetchEnlargedFontEnabled }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SecureInboxSummary);
